import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShopError, api, type ShopListItem, type SortKey } from '../api';
import { useReveal } from '../motion';
import { track } from '../tracking/tracker';
import { bool, num, str } from './types';
import { ProductCard } from './ProductCard';
import { CategoryNav } from './CategoryNav';
import { ParameterFacets } from './ParameterFacets';
import { itemWord } from '../plural';

/** Povolene klice razeni. Neznama hodnota v URL spadne na vychozi. */
const SORT_KEYS: readonly SortKey[] = ['relevance', 'price_asc', 'price_desc', 'newest', 'rating', 'name'];

/**
 * CDP dedup posledniho trackovaneho hledani. Na urovni modulu schvalne:
 * kdyby vyloha mela dve mrizky zbozi, dotaz se zapocita jen jednou.
 * Debounce SearchBoxu se neresi – vazeme se na URL a dobehla data.
 */
let lastTrackedSearch = '';

/**
 * Mrizka zbozi.
 *
 * Hledani a filtr kategorii zustavaji v URL, ne ve stavu komponenty:
 * odkaz na vyfiltrovany katalog musi jit poslat a musi fungovat i zpetne
 * tlacitko v prohlizeci.
 */
export function ProductGridBlock({ config }: { config: Record<string, unknown> }) {
  const [params, setParams] = useSearchParams();
  const search = params.get('hledat') ?? '';
  const category = params.get('kategorie') ?? '';
  // Neduverujeme URL: cizi hodnota `razeni` by jinak sla na API a to by
  // ji odmitlo (400) – misto vysledku by zakaznik videl chybu. Padneme
  // radeji na vychozi razeni.
  const sortParam = params.get('razeni');
  const sort: SortKey =
    sortParam !== null && (SORT_KEYS as readonly string[]).includes(sortParam)
      ? (sortParam as SortKey)
      : 'relevance';
  const inStock = params.get('skladem') === '1';
  const brand = params.get('znacka') ?? '';
  /*
   * Parametricke filtry, `?parametry=prichut:kureci,rybi;vek:stene`.
   *
   * Stejne jako hledani a kategorie zijou v URL, ne ve stavu komponenty:
   * vyfiltrovany katalog musi jit poslat odkazem a zpetne tlacitko musi
   * fungovat. Neznamy zapis server odmitne prazdnym vysledkem (ne 400),
   * takze cizi hodnota v adrese mrizku nerozbije.
   */
  const parametry = params.get('parametry') ?? '';

  const showSearch = bool(config, 'showSearch', true);
  const showCategories = bool(config, 'showCategories', true);
  const limit = num(config, 'limit', 24);
  const title = str(config, 'title', 'Naše zboží');

  const [items, setItems] = useState<readonly ShopListItem[]>([]);
  /**
   * KOLIK ZBOZI JE CELKEM, ne kolik ho je zrovna nactene.
   *
   * Popisek driv psal `items.length`, tedy vzdy velikost jedne stranky.
   * Na katalogu o 69 kusech to nikdo nepoznal; po importu ma kategorie
   * „Pamlsky" 1 466 polozek a zakaznik by se dozvedel, ze jich je 24 —
   * a k ostatnim by se nedostal.
   */
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [brands, setBrands] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const { ref, className } = useReveal<HTMLElement>();

  useEffect(() => {
    void api
      .listBrands()
      .then((result) => setBrands(result.brands))
      .catch(() => setBrands([]));
  }, []);

  // Zmena filtru znamena jiny vypis – zacina se od zacatku.
  useEffect(() => {
    setOffset(0);
  }, [search, category, sort, inStock, brand, parametry, limit]);

  useEffect(() => {
    const id = ++requestId.current;
    const first = offset === 0;
    const timer = setTimeout(
      () => {
        // Pri dokladani se mrizka NESKRYVA – jinak by zakaznikovi zmizelo
        // pod rukama to, co uz cetl, a stranka by mu poskocila.
        if (first) setLoading(true);
        else setLoadingMore(true);
        api
          .listProducts({ search, category, sort, inStock, brand, parametry, limit, offset })
          .then((result) => {
            // Pomala odpoved na starsi hledani nesmi prepsat aktualni vysledky.
            if (id !== requestId.current) return;
            setItems((prev) => (first ? result.items : [...prev, ...result.items]));
            setTotal(result.total);
            setError(null);
          })
          .catch((caught: unknown) => {
            if (id !== requestId.current) return;
            setError(caught instanceof ShopError ? caught.message : 'Načtení se nezdařilo.');
          })
          .finally(() => {
            if (id !== requestId.current) return;
            setLoading(false);
            setLoadingMore(false);
          });
      },
      search === '' || !first ? 0 : 250,
    );
    return () => clearTimeout(timer);
  }, [search, category, sort, inStock, brand, parametry, limit, offset]);

  // CDP: product_searched az po dobehnuti dat (results_count musi byt realny).
  useEffect(() => {
    if (search === '' || loading || search === lastTrackedSearch) return;
    lastTrackedSearch = search;
    track('product_searched', { query: search, results_count: items.length });
  }, [search, items, loading]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  return (
    <section ref={ref} className={`block products ${className}`} id="zbozi">
      <div className="block-head">
        {title !== '' && <h2 className="block-title">{title}</h2>}
        {!loading && items.length > 0 && (
          <span className="muted small">
            {items.length < total
              ? `${items.length} z ${total} ${itemWord(total)}`
              : `${total} ${itemWord(total)}`}
          </span>
        )}
      </div>

      {(showSearch || showCategories) && (
        <div className="filters">
          {showSearch && (
            <input
              type="search"
              className="search"
              placeholder="Hledat zboží…"
              value={search}
              onChange={(event) => updateParam('hledat', event.target.value)}
            />
          )}
          {/*
            Rozcestnik misto plocheho pasu vsech kategorii: ukazuje vzdycky jen
            jednu uroven a drobeckovou cestu. Plochy vypis byl pri 82
            kategoriich dlouhy, pri 204 z importu by byl neovladatelny —
            a treti a ctvrta uroven by v nem stejne splynula s prvni.
          */}
          {showCategories && (
            <CategoryNav active={category} onPick={(slug) => updateParam('kategorie', slug)} />
          )}
        </div>
      )}

      {/*
        Parametricke filtry se vykresli jen tam, kde je z ceho vybirat —
        parametry ma zhruba petina zverejneneho katalogu. Kdyz v tomhle
        vysledku nic nefiltruji, komponenta nevrati nic.
      */}
      <ParameterFacets
        search={search}
        category={category}
        brand={brand}
        inStock={inStock}
        value={parametry}
        total={total}
        onChange={(next) => updateParam('parametry', next)}
      />

      <div className="catalog-toolbar">
        <label className="sort-field">
          <span className="visually-hidden">Řadit</span>
          <select value={sort} onChange={(event) => updateParam('razeni', event.target.value)}>
            <option value="relevance">Doporučujeme</option>
            <option value="price_asc">Nejlevnější</option>
            <option value="price_desc">Nejdražší</option>
            <option value="rating">Nejlépe hodnocené</option>
            <option value="newest">Nejnovější</option>
            <option value="name">Podle názvu</option>
          </select>
        </label>

        {brands.length > 0 && (
          <label className="sort-field">
            <span className="visually-hidden">Značka</span>
            <select value={brand} onChange={(event) => updateParam('znacka', event.target.value)}>
              <option value="">Všechny značky</option>
              {brands.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="stock-toggle">
          <input
            type="checkbox"
            checked={inStock}
            onChange={(event) => updateParam('skladem', event.target.checked ? '1' : '')}
          />
          <span>Jen skladem</span>
        </label>
      </div>

      {error !== null && <p className="alert">{error}</p>}

      {/* Kostry misto tocitka: stranka pri nacitani nepodskoci. */}
      {loading && items.length === 0 && (
        <div className="grid" aria-hidden="true">
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="card skeleton-card" style={{ '--i': index } as React.CSSProperties}>
              <div className="skeleton thumb" />
              <div className="product-body">
                <div className="skeleton line short" />
                <div className="skeleton line" />
                <div className="skeleton line tiny" />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid">
        {items.map((product, index) => (
          <ProductCard key={product.id} product={product} index={index} />
        ))}
      </div>

      {items.length === 0 && !loading && (
        <p className="empty muted">
          {parametry !== ''
            ? 'Žádné zboží neodpovídá zvoleným filtrům. Zkus některý z nich zrušit.'
            : search === '' && category === ''
              ? 'Zatím tu není žádné zboží.'
              : 'Nic jsme nenašli. Zkus jiné hledání.'}
        </p>
      )}

      {/*
        Bez tohohle tlacitka koncil vypis na prvni strance: kategorie
        „Pamlsky" ma 1 466 polozek a zakaznik jich videl 24, bez moznosti
        dostat se dal. Dokladani (ne strankovani cisly) drzi pozici
        rolovani – zakaznik neztrati to, co uz prohlizel.
      */}
      {items.length > 0 && items.length < total && (
        <div className="grid-more">
          <button
            type="button"
            className="btn ghost"
            onClick={() => setOffset(items.length)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Načítám…' : `Zobrazit další (zbývá ${total - items.length})`}
          </button>
        </div>
      )}
    </section>
  );
}
